import React, { useState, useRef, useEffect } from "react";
import { assets } from "../assets/assets";
import countries from "../data/CountryCodes.json";

interface Country {
  name: string;
  code: string;
  emoji: string;
  image: string;
  dialCodes?: string[];
  slug?: string;
}

interface PhoneValue {
  countryCode: string;
  phoneNumber: string;
}

interface PhoneInputProps {
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  error?: string;
  className?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  error,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    countries.find((ele) => ele.dialCodes?.[0] === value.countryCode) ||
      countries[0]
  );

  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country?.dialCodes?.[0].includes(searchQuery) ||
      country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    onChange({ ...value, countryCode: country.dialCodes?.[0] || "+91" });
    setIsOpen(false);
    setSearchQuery("");
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = e.target.value.replace(/\D/g, "");
    onChange({
      countryCode: selectedCountry?.dialCodes?.[0] || "+91",
      phoneNumber: phoneNumber,
    });
  };

  return (
    <>
         <div className={`border-blue-900 flex ${className}`}>
        <div
          className="relative flex-shrink-0"
          ref={dropdownRef}
        >
          <div
            onClick={() =>  setIsOpen(!isOpen)}
            className={`flex items-center justify-between px-3 py-2 border rounded-l bg-white cursor-pointer
              ${error ? "border-red-500" : "border-gray-300"}
              min-w-0 w-auto max-w-none`}
          >
            <span className="flex items-center gap-2 min-w-0 w-auto max-w-none">
              <img
                src={selectedCountry.image}
                alt={`${selectedCountry.name} flag`}
                className="w-8 h-5 object-cover rounded-sm block"
              />
              <span className="whitespace-nowrap">{selectedCountry?.dialCodes?.[0]}</span>
            </span>
          </div>

          {isOpen && (
            <div className="absolute z-10 w-72 mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
              <div className="p-2 border-b">
                <div className="flex items-center px-2 bg-gray-50 rounded">
                  <img src={assets.search_icon} alt="Search-Icon" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search countries..."
                    className="w-full p-2 bg-transparent outline-none"
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => handleCountrySelect(country)}
                    className="flex items-center w-full px-4 py-2 hover:bg-gray-100"
                  >
                    <img
                      src={country.image}
                      alt={`${country.name} flag`}
                      className="w-5 h-4 object-cover rounded-sm mr-2 block"
                    />
                    <span className="flex-1 text-left">{country.name}</span>
                    <span className="text-gray-500">
                      {country?.dialCodes?.[0]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="w-full">
          <input
            type="tel"
            value={value.phoneNumber}
            onChange={handlePhoneChange}
            placeholder="Phone number"
            className={`flex-1 px-3 w-full py-2 border-y border-r rounded-r ${
              error ? "border-red-500" : "border-gray-300"
            } focus:outline-none focus:ring-0`}
          />
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </>
  );
};

export default PhoneInput;
